import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";

export interface UvScale {
  readonly u: number;
  readonly v: number;
}

export interface BoxDimensions {
  readonly width: number;
  readonly height: number;
  readonly depth: number;
}

export interface VertexCoordinates {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface VertexUv {
  readonly u: number;
  readonly v: number;
}

export function calculateUvScale(
  worldWidth: number,
  worldHeight: number,
  tileWorldWidth = 4,
  tileWorldHeight = 3.6,
): UvScale {
  return {
    u: Math.max(0.125, worldWidth / tileWorldWidth),
    v: Math.max(0.125, worldHeight / tileWorldHeight),
  };
}

/**
 * Projects a box vertex onto its own face. Every quad therefore uses the two
 * physical dimensions that actually lie in that plane instead of inheriting
 * one material scale from the longest side of the complete box.
 */
export function calculateBoxVertexUv(
  position: VertexCoordinates,
  normal: VertexCoordinates,
  dimensions: BoxDimensions,
  horizontalTileWorldSize = 4,
  verticalTileWorldSize = 3.6,
): VertexUv {
  assertPositiveDimensions(dimensions, horizontalTileWorldSize, verticalTileWorldSize);

  const absoluteX = Math.abs(normal.x);
  const absoluteY = Math.abs(normal.y);
  const absoluteZ = Math.abs(normal.z);

  if (absoluteY >= absoluteX && absoluteY >= absoluteZ) {
    return {
      u: (position.x + dimensions.width / 2) / horizontalTileWorldSize,
      v:
        (normal.y >= 0 ? dimensions.depth / 2 - position.z : position.z + dimensions.depth / 2) /
        horizontalTileWorldSize,
    };
  }

  if (absoluteX >= absoluteZ) {
    return {
      u:
        (normal.x >= 0 ? dimensions.depth / 2 - position.z : position.z + dimensions.depth / 2) /
        horizontalTileWorldSize,
      v: (position.y + dimensions.height / 2) / verticalTileWorldSize,
    };
  }

  return {
    u:
      (normal.z >= 0 ? position.x + dimensions.width / 2 : dimensions.width / 2 - position.x) /
      horizontalTileWorldSize,
    v: (position.y + dimensions.height / 2) / verticalTileWorldSize,
  };
}

export function applyBoxQuadUvs(
  mesh: Mesh,
  dimensions: BoxDimensions,
  horizontalTileWorldSize = 4,
  verticalTileWorldSize = 3.6,
): void {
  const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
  const normals = mesh.getVerticesData(VertexBuffer.NormalKind);
  if (!positions || !normals || positions.length !== normals.length) {
    throw new Error(`Box mesh ${mesh.name} is missing compatible position and normal buffers`);
  }

  const uvs: number[] = [];
  for (let offset = 0; offset < positions.length; offset += 3) {
    const uv = calculateBoxVertexUv(
      {
        x: positions[offset] as number,
        y: positions[offset + 1] as number,
        z: positions[offset + 2] as number,
      },
      {
        x: normals[offset] as number,
        y: normals[offset + 1] as number,
        z: normals[offset + 2] as number,
      },
      dimensions,
      horizontalTileWorldSize,
      verticalTileWorldSize,
    );
    uvs.push(uv.u, uv.v);
  }
  mesh.setVerticesData(VertexBuffer.UVKind, uvs, false, 2);
}

function assertPositiveDimensions(
  dimensions: BoxDimensions,
  horizontalTileWorldSize: number,
  verticalTileWorldSize: number,
): void {
  if (
    dimensions.width <= 0 ||
    dimensions.height <= 0 ||
    dimensions.depth <= 0 ||
    horizontalTileWorldSize <= 0 ||
    verticalTileWorldSize <= 0
  ) {
    throw new RangeError("Box dimensions and UV tile sizes must be positive");
  }
}
